import { Fraga } from "@/types/riskbedomning";

/**
 * Disciplinuppdelning av riskfrågor.
 * Frågetexter och antal frågor ändras aldrig här – endast märkning och filtrering.
 */

export interface DisciplineDef {
  key: string;
  short: string;
  label: string;
}

export const GEMENSAM = "gemensam";

export const DISCIPLINES: DisciplineDef[] = [
  { key: "A", short: "A", label: "Arkitekt" },
  { key: "K", short: "K", label: "Konstruktion" },
  { key: "V", short: "V", label: "VVS" },
  { key: "E", short: "E", label: "El" },
  { key: "S", short: "S", label: "Styr och regler" },
  { key: "MA", short: "MA", label: "Maskin" },
  { key: "M", short: "M", label: "Mark" },
  { key: "G", short: "G", label: "Geoteknik" },
  { key: "BR", short: "BR", label: "Brand" },
  { key: "O", short: "Ö", label: "Övrigt" },
];

export const DISCIPLINE_LABELS: Record<string, string> = {
  ...Object.fromEntries(DISCIPLINES.map((d) => [d.key, d.label])),
  [GEMENSAM]: "Gemensam",
};

export const getDisciplineLabel = (key: string): string =>
  DISCIPLINE_LABELS[key] ?? key;

/** Explicit märkning per fråge-id (byggskede). */
const ID_MAP: Record<string, string[]> = {
  // Byggbarhet & konstruktion
  "fallrisker-stallningar-sammansatt": ["K", "A"],
  "tornkranar-lyft-sammansatt": ["K", "MA"],
  "schakt-mark-sammansatt": ["M", "G", "K"],
  "miljopaverkan-arbetsrotation": [GEMENSAM],
  "materialhantering-stadning": [GEMENSAM],

  // Drift & underhåll (byggskede)
  "drift-atkomst-fallskydd-sammansatt": ["A", "K", "V"],
  "drift-ergonomi-belysning": ["A", "E"],
  "drift-kemikalier-ventilation": ["V", "S"],
  "drift-buller-utrymning": ["A", "BR", "V"],
  "drift-underhall-arbetsmiljo": ["V", "E", "S"],

  // Elsäkerhet
  "loto-frankoppling": ["E", "S"],
  "elverktyg-mobil-utrustning": ["E"],
  ensamarbete: [GEMENSAM],
  maskinsakerhet: ["MA", "S"],
  "arbete-varme-kyla": ["V"],

  // Etablering & arbetsplatsorganisation
  "etablering-utrymmen-sammansatt": ["A", "V", "E"],
  "transport-logistik-sammansatt": ["M", GEMENSAM],
  "vader-yttre-forhallanden": [GEMENSAM],
  "boende-djur-omgivning": [GEMENSAM],

  // Planering & projektering
  "transport-material-sammansatt": ["K", GEMENSAM],
  "flaskhalsar-samordning": [GEMENSAM],
  "ergonomi-montage-sammansatt": ["K", "V", "E"],
  "stabilitet-haveri": ["K", "G"],

  // Säkerhetssystem & skydd
  "fordon-trafik-sammansatt": ["M"],
  "sakerhetsskydd-barriarer": ["A", "K"],
  "farliga-amnen-brand": ["BR", "V"],
  "forsta-hjalpen-ensamarbete": [GEMENSAM],
  "samordning-pse": [GEMENSAM],

  // Specialarbeten & miljörisker
  "osa-risker": [GEMENSAM],
  belastningsergonomi: [GEMENSAM],
  "tillfälliga-elinstallationer": ["E"],
  "belysning-arbetsplats": ["E"],
  "golv-halkrisk": ["A", "K"],
};

/** Nyckelordsmatchning (används för bruksskede, särskilda risker och egna frågor). */
const KEYWORD_MAP: Array<{ re: RegExp; disciplines: string[] }> = [
  { re: /el(säkerhet|installation)|elsakerhet|elinstallation|\bel\b/i, disciplines: ["E"] },
  { re: /ventilation|klimat|luftkvalit|vvs|rör|vatten|våtrum|hygien/i, disciplines: ["V"] },
  { re: /belysning|ljus/i, disciplines: ["E", "A"] },
  { re: /utrymning|brand|larm|sotning|heta arbeten/i, disciplines: ["BR"] },
  { re: /trafik|gods|mark|avfall|schakt/i, disciplines: ["M"] },
  { re: /lyft|hiss|rullande|maskin/i, disciplines: ["MA", "K"] },
  { re: /glas|fönster|fasad|städ|renhållning|tak/i, disciplines: ["A"] },
  { re: /trapp|entré|entrer|dörr|port|byggmaterial|konstruktion|stabilitet/i, disciplines: ["A", "K"] },
  { re: /styr|övervakning|regler/i, disciplines: ["S"] },
  { re: /geotekn|grundvatten|sättning/i, disciplines: ["G"] },
  { re: /drift|underhåll|underhall/i, disciplines: ["V", "E"] },
  { re: /ljud|buller/i, disciplines: ["A", "V"] },
];

const normalize = (list: string[]): string[] =>
  Array.from(new Set(list.filter(Boolean)));

/** Returnerar disciplinerna för en fråga. Tom lista tolkas som gemensam. */
export const getQuestionDisciplines = (fraga: Fraga): string[] => {
  if (fraga.disciplines && fraga.disciplines.length > 0) {
    return normalize(fraga.disciplines);
  }

  const explicit = ID_MAP[fraga.id];
  if (explicit) return normalize(explicit);

  // Duplicerade frågor ärver originalets märkning
  const baseId = fraga.id.split("_duplicate_")[0];
  if (baseId !== fraga.id && ID_MAP[baseId]) return normalize(ID_MAP[baseId]);

  const haystack = `${fraga.id} ${fraga.kategori ?? ""} ${fraga.text ?? ""}`;
  const matched: string[] = [];
  KEYWORD_MAP.forEach(({ re, disciplines }) => {
    if (re.test(haystack)) matched.push(...disciplines);
  });

  return matched.length > 0 ? normalize(matched) : [GEMENSAM];
};

export const isCommonQuestion = (fraga: Fraga): boolean => {
  const d = getQuestionDisciplines(fraga);
  return d.length === 0 || d.includes(GEMENSAM);
};

/**
 * Matchar en fråga mot valda discipliner.
 * Inga valda discipliner = visa allt. Gemensamma frågor visas alltid.
 */
export const matchesDisciplines = (
  fraga: Fraga,
  selected: string[] | undefined,
  includeCommon = true
): boolean => {
  if (!selected || selected.length === 0) return true;
  const disciplines = getQuestionDisciplines(fraga);
  if (includeCommon && disciplines.includes(GEMENSAM)) return true;
  return disciplines.some((d) => selected.includes(d));
};

export const filterByDisciplines = (
  fragor: Fraga[],
  selected: string[] | undefined,
  includeCommon = true
): Fraga[] => fragor.filter((f) => matchesDisciplines(f, selected, includeCommon));

/** Kort etikett för export/tabeller, t.ex. "K, VVS" eller "Gemensam". */
export const formatDisciplines = (fraga: Fraga): string =>
  getQuestionDisciplines(fraga)
    .map((d) => getDisciplineLabel(d))
    .join(", ");

/** Sammanställning per disciplin – används av Bas-P:s samordningsvy. */
export interface DisciplineSummary {
  key: string;
  label: string;
  total: number;
  answered: number;
  unanswered: number;
}

export const summarizeByDiscipline = (
  fragor: Fraga[],
  svar: Record<string, any> = {}
): DisciplineSummary[] => {
  const keys = [...DISCIPLINES.map((d) => d.key), GEMENSAM];
  const buckets: Record<string, DisciplineSummary> = Object.fromEntries(
    keys.map((k) => [
      k,
      { key: k, label: getDisciplineLabel(k), total: 0, answered: 0, unanswered: 0 },
    ])
  );

  fragor.forEach((fraga) => {
    const answered = Boolean(svar?.[fraga.id]?.svar);
    getQuestionDisciplines(fraga).forEach((d) => {
      const bucket = buckets[d] ?? buckets[GEMENSAM];
      bucket.total += 1;
      if (answered) bucket.answered += 1;
      else bucket.unanswered += 1;
    });
  });

  return keys.map((k) => buckets[k]).filter((b) => b.total > 0);
};
